import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { useSearchParams } from '@remix-run/react';
import { pickBy } from 'es-toolkit';

interface Props {
  showLoggedInToast: boolean;
}

// As we move to relying on flash based notifications, we should remove this hook entirely main
const useNotifications = ({ showLoggedInToast }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [toastShown, setToastShown] = useState(false);
  const subscribeParam = searchParams.get('subscription');
  const optInEmailParam = searchParams.get('opt_in_email');
  const logoutParam = searchParams.get('logout');
  const { t } = useTranslation();

  const status = searchParams.get('toast');
  const message = searchParams.get('message');
  const title = searchParams.get('title');
  const toastId = searchParams.get('toastId');

  // Render toast and then delete the query params
  useEffect(() => {
    if (status && message) {
      const toastMessage = `${title ? `${title}\n` : ''}${message}`;
      const toastOptions = pickBy({ id: toastId || undefined }, (value) => value !== undefined);
      status === 'success' ? toast.success(toastMessage, toastOptions) : toast.error(toastMessage, toastOptions);
      searchParams.delete('toast');
      searchParams.delete('message');
      searchParams.delete('title');
      searchParams.delete('toastId');
      setSearchParams(searchParams, { replace: true, preventScrollReset: true });
      setToastShown(true);
    }
  }, [status, message, title, toastId, searchParams, setSearchParams]);

  // Subscription Notifications
  useEffect(() => {
    if (subscribeParam) {
      if (subscribeParam === 'success') {
        toast.success(t('notifications.subscribe.success'));
      } else if (subscribeParam === 'error') {
        toast.error(t('notifications.subscribe.error'));
      }

      // Clear param from url
      searchParams.delete('subscription');
      setSearchParams(searchParams);
    }
  }, [subscribeParam, searchParams, setSearchParams, t]);

  // Logout
  useEffect(() => {
    if (logoutParam) {
      if (logoutParam === 'success') {
        toast.success(t('notifications.logout.success'));
      }

      // Clear param from url
      searchParams.delete('logout');
      setSearchParams(searchParams);
    }
  }, [logoutParam, searchParams, setSearchParams, t]);

  // Optin Email Notifications
  useEffect(() => {
    if (optInEmailParam) {
      if (optInEmailParam === 'error') {
        toast.success(t('notifications.optin.error'));

        // Clear param from url
        searchParams.delete('opt_in_email');
        setSearchParams(searchParams);
      }
    }
  }, [optInEmailParam, searchParams, setSearchParams, t]);

  useEffect(() => {
    // showLoggedInToast if there isn't already a toast requested
    // Also check that we don't have toast params in the URL
    if (showLoggedInToast && !toastShown && !status && !message) {
      toast.success(t('login.success.title'));
    }
  }, [showLoggedInToast, t, toastShown, status, message]);
};

export default useNotifications;
